// src/components/EmailVerificationModal.tsx
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonIcon,
  IonSpinner,
  useIonToast,
  IonInput,
  IonItem,
  IonLabel,
  IonText,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import React, { useState } from 'react';
import axios from 'axios';

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerificationSuccess: (sessionToken: string) => void;
  shareToken: string;
  profileTitle?: string;
}

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  onClose,
  onVerificationSuccess,
  shareToken,
  profileTitle
}) => {
  const [present] = useIonToast();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  const handleSendCode = async () => {
    if (!email || !email.includes('@')) {
      present({ message: 'Please enter a valid email address', duration: 3000, color: 'danger' });
      return;
    }

    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.autotuneresume.com/api/v1';
      await axios.post(`${apiUrl}/public/profile/${shareToken}/request-access`, {
        email: email
      });

      present({ message: 'Verification code sent to your email!', duration: 3000, color: 'success' });
      setStep('code');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to send verification code';
      present({ message, duration: 3000, color: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      present({ message: 'Please enter the 6-digit verification code', duration: 3000, color: 'danger' });
      return;
    }

    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.autotuneresume.com/api/v1';
      const response = await axios.post(`${apiUrl}/public/profile/${shareToken}/verify-access`, {
        email: email,
        verification_code: verificationCode
      });

      if (response.data.success && response.data.session_token) {
        present({ message: 'Email verified successfully!', duration: 2000, color: 'success' });
        onVerificationSuccess(response.data.session_token);
        onClose();
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Invalid verification code';
      present({ message, duration: 3000, color: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.autotuneresume.com/api/v1';
      await axios.post(`${apiUrl}/public/profile/${shareToken}/request-access`, {
        email: email
      });

      present({ message: 'New verification code sent!', duration: 3000, color: 'success' });
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to resend verification code';
      present({ message, duration: 3000, color: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const handleModalDismiss = () => {
    setStep('email');
    setEmail('');
    setVerificationCode('');
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleModalDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Access Required</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleModalDismiss}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <IonSpinner />
          </div>
        ) : step === 'email' ? (
          <div>
            <IonText>
              <h2>Email Verification Required</h2>
              <p>
                {profileTitle ? `"${profileTitle}"` : 'This resume profile'} requires email verification to access.
              </p>
            </IonText>

            <IonItem style={{ marginTop: '24px' }}>
              <IonLabel position="stacked">Email Address</IonLabel>
              <IonInput
                type="email"
                value={email}
                placeholder="Enter your email address"
                onIonInput={(e) => setEmail(e.detail.value!)}
              />
            </IonItem>

            <IonText>
              <p style={{ fontSize: '0.9rem', color: 'var(--app-text-color-secondary)', marginTop: '16px' }}>
                A verification code will be sent to verify your identity.
              </p>
            </IonText>

            <IonButton
              expand="block"
              onClick={handleSendCode}
              style={{ marginTop: '24px' }}
              disabled={!email || loading}
            >
              Send Code
            </IonButton>
          </div>
        ) : (
          <div>
            <IonText>
              <h2>Enter Verification Code</h2>
              <p>We sent a code to {email}</p>
            </IonText>

            <IonItem style={{ marginTop: '24px' }}>
              <IonLabel position="stacked">Verification Code</IonLabel>
              <IonInput
                type="text"
                value={verificationCode}
                placeholder="Enter 6-digit code"
                maxlength={6}
                onIonInput={(e) => setVerificationCode(e.detail.value!)}
              />
            </IonItem>

            <IonButton
              expand="block"
              onClick={handleVerifyCode}
              style={{ marginTop: '24px' }}
              disabled={verificationCode.length !== 6 || loading}
            >
              Verify
            </IonButton>

            <IonButton
              expand="block"
              fill="outline"
              onClick={handleResendCode}
              style={{ marginTop: '16px' }}
              disabled={loading}
            >
              Resend Code
            </IonButton>

            <IonButton
              expand="block"
              fill="clear"
              onClick={() => setStep('email')}
              style={{ marginTop: '8px' }}
            >
              Change Email
            </IonButton>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default EmailVerificationModal;